/**
 * The iOS app writes copies with expo-clipboard, which rebuilds the HTML through
 * NSAttributedString and drops the `data-*` attributes that carry math and the
 * ProseMirror slice. The editor keeps its last such copy, so pasting it back into
 * the same WebView restores the original HTML instead of the rebuilt one.
 */
let lastCopy: { html: string; textKey: string } | undefined;

export function rememberAppClipboardCopy(html: string) {
  forgetAppClipboardCopy();

  const textKey = toTextKey(htmlToText(html));
  // An empty copy would match every paste without text, e.g. a lone image.
  if (!textKey) {
    return;
  }

  lastCopy = { html, textKey };
  // The text alone can't tell the copy from a later one with the same characters,
  // so it's dropped as soon as anything else may have replaced it: a copy in this
  // WebView fires `copy`/`cut`, and one in another app needs this one backgrounded.
  document.addEventListener("copy", forgetAppClipboardCopy, true);
  document.addEventListener("cut", forgetAppClipboardCopy, true);
  document.addEventListener("visibilitychange", forgetWhenHidden);
}

/** The original HTML when `html` is the app's last copy as rebuilt by iOS. */
export function recallAppClipboardCopy(html: string) {
  if (!lastCopy || html.includes("data-pm-slice")) {
    return undefined;
  }

  return toTextKey(htmlToText(html)) === lastCopy.textKey ? lastCopy.html : undefined;
}

function forgetAppClipboardCopy() {
  lastCopy = undefined;
  document.removeEventListener("copy", forgetAppClipboardCopy, true);
  document.removeEventListener("cut", forgetAppClipboardCopy, true);
  document.removeEventListener("visibilitychange", forgetWhenHidden);
}

function forgetWhenHidden() {
  if (document.visibilityState === "hidden") {
    forgetAppClipboardCopy();
  }
}

function htmlToText(html: string) {
  return new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";
}

// iOS restyles the rebuilt HTML and reflows its whitespace; only the characters survive.
function toTextKey(text: string) {
  return text.replace(/\s+/g, "");
}
